// STRAP WATCH CONFIGURATOR (Mario's canonical name) — lives on Strap-PDPs, shows "Compatible Watches"
// Legacy filename: watch-configurator-v2.js. Custom element: <watch-strap-configurator-v2>.
// Sister script for Watch PDPs: assets/watch-configurator.js
if (!customElements.get('watch-strap-configurator-v2')) {
	class WatchStrapConfiguratorV2 extends HTMLElement {
		constructor() {
			super()

			this.swiperContainer = this.querySelector('.strap-swiper')
			this.styleTabs = this.querySelectorAll('.watch-style-tab')
			this.faceTabs = this.querySelectorAll('.watch-face-tab')
			this.strapSlides = this.querySelectorAll('.strap-slide')
			this.paginationEl = this.querySelector('.strap-pagination')

			this.styleTablist = this.querySelector('.watch-styles-tabs')
			this.faceTablist = this.querySelector('.watch-faces-tabs')

			this.watchOnlyForm = this.querySelector('.watch-only-form')
			this.strapOnlyForm = this.querySelector('.strap-only-form')
			this.bundleForm = this.querySelector('.bundle-form')
			this.notifyLink = this.querySelector('.strap-notify-link')

			this.locale = this.dataset.locale || 'en-GB'
			this.currency = this.dataset.currency || 'GBP'
			this.mode = this.dataset.mode === 'watches' ? 'watches' : 'straps'
			this.fixedStrapDataEl = this.querySelector('.fixed-strap-data')

			if (this.bundleForm) {
				const bundleBtn = this.bundleForm.querySelector('button')
				if (bundleBtn && !bundleBtn.dataset.baseText) {
					bundleBtn.dataset.baseText = bundleBtn.textContent.trim()
				}
			}

			this.numberOfSlides = this.querySelectorAll('.swiper-slide').length
			this.initialSlide = this.numberOfSlides > 5 ? 2 : 0

			this.state = {
				currentStyle: '',
				currentFace: '',
				currentWatchId: '',
				currentWatchVariantId: '',
				currentWatchPriceCents: 0,
				currentWatchAvailable: true,
				currentWatchDiscontinued: false,
				currentWatchUrl: '',
				currentWatchName: '',
				currentStrapId: '',
				currentStrapVariantId: '',
				currentStrapPriceCents: 0,
				currentStrapAvailable: true,
				currentStrapUrl: '',
				currentStrapName: ''
			}

			if (this.mode === 'watches' && this.fixedStrapDataEl) {
				const el = this.fixedStrapDataEl
				this.state.currentStrapId = el.dataset.productId || ''
				this.state.currentStrapVariantId = el.dataset.variantId || ''
				this.state.currentStrapPriceCents = parseInt(el.dataset.priceCents, 10) || 0
				this.state.currentStrapAvailable = el.dataset.available !== 'false'
				this.state.currentStrapUrl = el.dataset.productUrl || ''
				this.state.currentStrapName = el.dataset.productTitle || ''
			}

			this.swiper = null
			this.swiperInitialized = false

			this.visibleSlidesMap = new Map()

			this.pendingTimeouts = new Set()
			this.pendingRAFs = new Set()

			this._onResize = this.handleResize.bind(this)
			this._onSectionLoad = this.init.bind(this)
			this._onDOMReady = this.init.bind(this)
			this._onStyleTablistKey = this.handleTablistKeydown.bind(this, 'style')
			this._onFaceTablistKey = this.handleTablistKeydown.bind(this, 'face')

			this.swiperOptions = {
				a11y: false,
				initialSlide: this.initialSlide,
				threshold: 3,
				spaceBetween: 9,
				slidesPerView: 5,
				slidesPerGroup: 1,
				resistanceRatio: 0.72,
				centeredSlides: true,
				slideToClickedSlide: true,
				loop: false,
				navigation: {
					enabled: true,
					nextEl: '.swiper-button-next',
					prevEl: '.swiper-button-prev',
				},
				pagination: false,
				breakpoints: {
					320: {
						navigation: { enabled: false },
						slidesPerView: 1.8,
						slidesPerGroup: 1,
						initialSlide: 0
					},
					768: {
						enabled: true,
						slidesPerView: 3,
						slidesPerGroup: 1,
						initialSlide: this.initialSlide
					},
					1200: {
						enabled: true,
						slidesPerView: 5,
						slidesPerGroup: 1,
						initialSlide: this.initialSlide
					}
				},
				on: {
					slideChange: () => {
						this.hydrateVisibleSlides()
						this.updateActiveStrapInfo()
						this.updateCustomPagination()
					},
					init: () => {
						this.hydrateVisibleSlides()
						this.scheduleBackgroundHydration()
						this.setManagedTimeout(() => {
							if (!this.swiper) return
							this.updateVisibleSlidesMap()
							this.updateActiveStrapInfo()
							this.renderCustomPagination()
							this.updateCustomPagination()
						}, 100)
					}
				}
			}
		}

		setManagedTimeout(fn, ms) {
			const id = setTimeout(() => {
				this.pendingTimeouts.delete(id)
				fn()
			}, ms)
			this.pendingTimeouts.add(id)
			return id
		}

		setManagedRAF(fn) {
			const id = requestAnimationFrame(() => {
				this.pendingRAFs.delete(id)
				fn()
			})
			this.pendingRAFs.add(id)
			return id
		}

		cancelManagedAsync() {
			this.pendingTimeouts.forEach(id => clearTimeout(id))
			this.pendingTimeouts.clear()
			this.pendingRAFs.forEach(id => cancelAnimationFrame(id))
			this.pendingRAFs.clear()
		}

		handleResize() {
			if (!this.swiperInitialized) return
			if (this._resizeTimeoutId) {
				clearTimeout(this._resizeTimeoutId)
				this.pendingTimeouts.delete(this._resizeTimeoutId)
			}
			this._resizeTimeoutId = this.setManagedTimeout(() => {
				this._resizeTimeoutId = null
				if (!this.swiper || !this.swiperInitialized) return
				this.updateSwiperOptions()
				this.swiper.update()
				this.updateVisibleSlidesMap()
				this.updateActiveStrapInfo()
				this.renderCustomPagination()
				this.updateCustomPagination()
			}, 150)
		}

		formatMoney(cents, currency) {
			const amount = (parseInt(cents, 10) || 0) / 100
			const code = currency || this.currency
			try {
				return new Intl.NumberFormat(this.locale, {
					style: 'currency',
					currency: code,
					minimumFractionDigits: 0,
					maximumFractionDigits: 2
				}).format(amount)
			} catch (e) {
				return `${code} ${amount.toFixed(2)}`
			}
		}

		calculateInitialSlide(visibleSlidesCount) {
			return visibleSlidesCount > 5 ? 2 : 0
		}

		hydrateSlide(slide) {
			if (!slide || slide.dataset.hydrated === 'true') return
			const templates = slide.querySelectorAll('template.lazy-img-tpl')
			if (!templates.length) {
				slide.dataset.hydrated = 'true'
				return
			}
			templates.forEach(tpl => {
				const parent = tpl.parentNode
				if (!parent) return
				while (tpl.content.firstChild) {
					parent.appendChild(tpl.content.firstChild)
				}
				tpl.remove()
			})
			slide.dataset.hydrated = 'true'
		}

		hydrateVisibleSlides() {
			if (!this.swiper) return
			const slides = Array.from(this.swiperContainer.querySelectorAll('.swiper-slide'))
			if (!slides.length) return
			const active = this.swiper.activeIndex || 0
			const view = Math.ceil(this.swiper.params.slidesPerView) || 1
			const start = Math.max(0, active - 1)
			const end = Math.min(slides.length - 1, active + view + 1)
			for (let i = start; i <= end; i++) {
				this.hydrateSlide(slides[i])
			}
		}

		scheduleBackgroundHydration() {
			if (this._bgHydrationScheduled) return
			this._bgHydrationScheduled = true
			const start = () => {
				const remaining = Array.from(this.swiperContainer.querySelectorAll('.swiper-slide:not([data-hydrated="true"])'))
				const step = () => {
					if (!remaining.length) return
					this.hydrateSlide(remaining.shift())
					this.setManagedTimeout(step, 180)
				}
				step()
			}
			if ('IntersectionObserver' in window) {
				const io = new IntersectionObserver((entries, observer) => {
					if (entries.some(e => e.isIntersecting)) {
						observer.disconnect()
						start()
					}
				}, { rootMargin: '300px' })
				io.observe(this)
				this._hydrationObserver = io
			} else {
				start()
			}
		}

		updateSwiperOptions() {
			const visibleSlidesCount = this.getVisibleStraps().length
			this.initialSlide = this.calculateInitialSlide(visibleSlidesCount)
			this.swiperOptions.initialSlide = this.initialSlide
			this.swiperOptions.breakpoints[1200].initialSlide = this.initialSlide

			if (this.swiper && this.swiperInitialized) {
				this.swiper.params.initialSlide = this.initialSlide
				this.swiper.update()
			}
		}

		connectedCallback() {
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', this._onDOMReady, { once: true })
			} else {
				this.init()
			}
			if (typeof Shopify !== 'undefined' && Shopify.designMode) {
				document.addEventListener('shopify:section:load', this._onSectionLoad)
			}
		}

		init() {
			this.setupEventListeners()
			this.initializeFirstStyle()
		}

		clearPaginationEl() {
			if (!this.paginationEl) return
			while (this.paginationEl.firstChild) {
				this.paginationEl.removeChild(this.paginationEl.firstChild)
			}
		}

		renderCustomPagination() {
			if (!this.paginationEl || !this.swiper) return

			this.clearPaginationEl()

			const snapGrid = this.swiper.snapGrid || []
			const pageCount = snapGrid.length

			if (pageCount <= 1) {
				this.paginationEl.classList.remove('is-active')
				return
			}

			for (let i = 0; i < pageCount; i++) {
				const bullet = document.createElement('button')
				bullet.type = 'button'
				bullet.className = 'strap-pagination__bullet'
				bullet.dataset.page = String(i)
				bullet.setAttribute('aria-label', `Go to page ${i + 1}`)
				bullet.addEventListener('click', () => {
					const page = parseInt(bullet.dataset.page, 10)
					if (this.swiper && this.swiper.snapGrid && this.swiper.snapGrid[page] !== undefined) {
						this.swiper.slideTo(page)
					}
				})
				this.paginationEl.appendChild(bullet)
			}
			this.paginationEl.classList.add('is-active')
		}

		updateCustomPagination() {
			if (!this.paginationEl || !this.swiper) return
			const activeIdx = this.swiper.activeIndex
			this.paginationEl.querySelectorAll('.strap-pagination__bullet').forEach((bullet, i) => {
				bullet.classList.toggle('is-active', i === activeIdx)
			})
		}

		setupEventListeners() {
			this.styleTabs.forEach(tab => {
				tab.addEventListener('click', event => this.handleStyleChange(event))
			})

			this.faceTabs.forEach(tab => {
				tab.addEventListener('click', event => this.handleFaceChange(event))
			})

			if (this.styleTablist) {
				this.styleTablist.addEventListener('keydown', this._onStyleTablistKey)
			}
			if (this.faceTablist) {
				this.faceTablist.addEventListener('keydown', this._onFaceTablistKey)
			}

			window.addEventListener('resize', this._onResize)

			if (typeof Shopify !== 'undefined' && Shopify.designMode) {
				this.parent = this.closest('section')
				this.observer = new MutationObserver(() => {
					if (this.swiper) {
						this.updateSwiperOptions()
						this.swiper.update()
						this.setManagedTimeout(() => {
							if (!this.swiper || !this.swiperInitialized) return
							this.updateVisibleSlidesMap()
							this.updateActiveStrapInfo()
							this.renderCustomPagination()
							this.updateCustomPagination()
						}, 100)
					}
				})
				this.observer.observe(this.parent, { childList: true, subtree: true })
			}
		}

		handleTablistKeydown(kind, event) {
			const tabs = kind === 'style'
				? Array.from(this.styleTabs).filter(t => t.style.display !== 'none')
				: Array.from(this.faceTabs).filter(t => t.style.display !== 'none')

			if (tabs.length === 0) return

			const current = document.activeElement
			const currentIdx = tabs.indexOf(current)
			let nextIdx = -1

			switch (event.key) {
				case 'ArrowRight':
				case 'ArrowDown':
					nextIdx = currentIdx >= 0 ? (currentIdx + 1) % tabs.length : 0
					break
				case 'ArrowLeft':
				case 'ArrowUp':
					nextIdx = currentIdx > 0 ? currentIdx - 1 : tabs.length - 1
					break
				case 'Home':
					nextIdx = 0
					break
				case 'End':
					nextIdx = tabs.length - 1
					break
				case 'Enter':
				case ' ':
					if (current && tabs.includes(current)) {
						event.preventDefault()
						current.click()
					}
					return
				default:
					return
			}

			if (nextIdx >= 0 && tabs[nextIdx]) {
				event.preventDefault()
				tabs[nextIdx].focus()
			}
		}

		updateTabAria(activeTab, tablist) {
			if (!tablist) return
			const tabs = tablist.querySelectorAll('[role="tab"]')
			tabs.forEach(tab => {
				const isActive = tab === activeTab
				tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
				tab.setAttribute('tabindex', isActive ? '0' : '-1')
			})
		}

		initializeFirstStyle() {
			if (this.styleTabs.length > 0) {
				this.styleTabs[0].click()
			} else {
				this.updateVisibleStraps()
			}
		}

		handleStyleChange(event) {
			const style = event.currentTarget.dataset.style
			this.state.currentStyle = style

			this.styleTabs.forEach(tab => tab.classList.remove('active'))
			event.currentTarget.classList.add('active')
			this.updateTabAria(event.currentTarget, this.styleTablist)

			this.destroySwiper()

			if (this.mode === 'straps') {
				let firstVisibleFaceTab = null
				let hasActiveFaceTab = false

				this.faceTabs.forEach(faceTab => {
					if (faceTab.dataset.style === style) {
						faceTab.style.display = 'block'
						if (!firstVisibleFaceTab) firstVisibleFaceTab = faceTab
						if (faceTab.classList.contains('active')) hasActiveFaceTab = true
					} else {
						faceTab.style.display = 'none'
					}
				})

				if (!hasActiveFaceTab && firstVisibleFaceTab) {
					firstVisibleFaceTab.classList.add('active')
					this.updateTabAria(firstVisibleFaceTab, this.faceTablist)
					this.handleFaceSelection(firstVisibleFaceTab)
				} else {
					const currentActiveFaceTab = this.querySelector('.watch-face-tab.active')
					if (currentActiveFaceTab && currentActiveFaceTab.dataset.style === style) {
						this.updateTabAria(currentActiveFaceTab, this.faceTablist)
						this.handleFaceSelection(currentActiveFaceTab)
					} else if (firstVisibleFaceTab) {
						firstVisibleFaceTab.classList.add('active')
						this.updateTabAria(firstVisibleFaceTab, this.faceTablist)
						this.handleFaceSelection(firstVisibleFaceTab)
					}
				}
			}

			this.updateSwiperOptions()
			this.updateVisibleStraps()
		}

		handleFaceChange(event) {
			this.destroySwiper()

			this.faceTabs.forEach(tab => tab.classList.remove('active'))
			event.currentTarget.classList.add('active')
			this.updateTabAria(event.currentTarget, this.faceTablist)
			this.handleFaceSelection(event.currentTarget)

			this.setManagedTimeout(() => {
				this.updateSwiperOptions()
				this.updateVisibleStraps()
			}, 50)
		}

		destroySwiper() {
			this.cancelManagedAsync()
			this._resizeTimeoutId = null
			if (this.swiper) {
				try { this.swiper.destroy(true, true) } catch (e) {}
				this.swiper = null
				this.swiperInitialized = false
				this.visibleSlidesMap.clear()
			}
			this.clearPaginationEl()
			if (this.paginationEl) {
				this.paginationEl.classList.remove('is-active')
			}
		}

		handleFaceSelection(faceTab) {
			this.state.currentFace = faceTab.dataset.face
			this.state.currentWatchId = faceTab.dataset.watchId
			this.state.currentWatchVariantId = faceTab.dataset.watchVariant || ''
			this.state.currentWatchPriceCents = parseInt(faceTab.dataset.watchPriceCents, 10) || 0
			this.state.currentWatchAvailable = faceTab.dataset.watchAvailable !== 'false'
			this.state.currentWatchDiscontinued = faceTab.dataset.watchDiscontinued === 'true'
			this.activateWatchFaces(this.state.currentFace)
			this.updatePurchaseForms()
		}

		activateWatchFaces(face) {
			this.querySelectorAll('.watch-face').forEach(watchFace => {
				watchFace.classList.toggle('active', watchFace.dataset.face === face)
			})
		}

		updateVisibleStraps() {
			this.getVisibleStraps()

			this.setManagedRAF(() => {
				this.setManagedTimeout(() => {
					this.initSwiper()
				}, 100)
			})
		}

		getVisibleStraps() {
			const visibleStraps = []
			this.visibleSlidesMap.clear()

			this.strapSlides.forEach(strap => {
				if (strap.dataset.style === this.state.currentStyle) {
					strap.style.display = 'block'
					visibleStraps.push(strap)
				} else {
					strap.style.display = 'none'
				}
			})

			return visibleStraps
		}

		updateVisibleSlidesMap() {
			if (!this.swiper) return

			this.visibleSlidesMap.clear()
			const visibleElements = this.querySelectorAll('.strap-slide[style*="block"]')

			visibleElements.forEach((element, swiperIndex) => {
				this.visibleSlidesMap.set(swiperIndex, element)
			})
		}

		initSwiper() {
			if (this.swiperInitialized) {
				this.swiper.destroy(true, true)
				this.swiperInitialized = false
			}

			const visibleSlides = this.getVisibleStraps()
			if (visibleSlides.length === 0) return

			this.swiper = new Swiper(this.swiperContainer, this.swiperOptions)

			this.swiper.wrapperEl.setAttribute('aria-live', 'polite')
			this.swiper.wrapperEl.id = `watch-swiper-${this.dataset.sectionId || Date.now()}`

			this.setManagedTimeout(() => {
				if (!this.swiper) return
				this.swiper.slides.forEach((slide, index) => {
					slide.setAttribute('role', 'group')
					slide.setAttribute('aria-label', `${index + 1} / ${this.swiper.slides.length}`)
					slide.dataset.index = index
				})

				this.updateVisibleSlidesMap()

				const navButtons = [
					this.swiper.navigation.prevEl,
					this.swiper.navigation.nextEl
				]

				navButtons.forEach((button, index) => {
					if (button) {
						const type = index === 0 ? 'Previous' : 'Next'
						button.setAttribute('aria-label', `${type} strap`)
						button.setAttribute('aria-controls', this.swiper.wrapperEl.id)
					}
				})

				this.swiperInitialized = true

				this.setManagedTimeout(() => {
					if (!this.swiper || !this.swiperInitialized) return
					this.updateActiveStrapInfo()
					this.renderCustomPagination()
					this.updateCustomPagination()
				}, 150)
			}, 50)
		}

		updateActiveStrapInfo() {
			if (!this.swiper || typeof this.swiper.realIndex === 'undefined') {
				const firstVisibleSlide = this.querySelector('.strap-slide[style*="block"]')
				if (firstVisibleSlide) {
					this.updateStrapInfo(firstVisibleSlide)
				}
				return
			}

			const swiperIndex = this.swiper.realIndex
			const activeSlideElement = this.visibleSlidesMap.get(swiperIndex)

			if (activeSlideElement) {
				this.updateStrapInfo(activeSlideElement)
			} else {
				const swiperSlide = this.swiper.slides[swiperIndex]
				if (swiperSlide) {
					const strapSlide = swiperSlide.classList.contains('strap-slide')
						? swiperSlide
						: swiperSlide.querySelector('.strap-slide')
					if (strapSlide && strapSlide.style.display !== 'none') {
						this.updateStrapInfo(strapSlide)
					}
				}
			}
		}

		updateStrapInfo(slideElement) {
			const strapInfo = this.querySelector('.strap-info')
			if (!strapInfo || !slideElement) return

			let activeName, activePriceCents, activeAvailable, activeUrl

			if (this.mode === 'watches') {
				activeName = slideElement.dataset.watchTitle || ''
				activePriceCents = parseInt(slideElement.dataset.watchPriceCents, 10) || 0
				activeAvailable = slideElement.dataset.watchAvailable !== 'false'
				activeUrl = slideElement.dataset.watchUrl || ''

				this.state.currentWatchId = slideElement.dataset.watchId || ''
				this.state.currentWatchVariantId = slideElement.dataset.watchVariant || ''
				this.state.currentWatchPriceCents = activePriceCents
				this.state.currentWatchAvailable = activeAvailable
				this.state.currentWatchDiscontinued = slideElement.dataset.watchDiscontinued === 'true'
				this.state.currentWatchUrl = activeUrl
				this.state.currentWatchName = activeName
			} else {
				activeName = slideElement.dataset.productTitle || ''
				activePriceCents = parseInt(slideElement.dataset.priceCents, 10) || 0
				activeAvailable = slideElement.dataset.available !== 'false'
				activeUrl = slideElement.dataset.productUrl || ''

				this.state.currentStrapVariantId = slideElement.dataset.variantId || ''
				this.state.currentStrapId = slideElement.dataset.productId || ''
				this.state.currentStrapPriceCents = activePriceCents
				this.state.currentStrapAvailable = activeAvailable
				this.state.currentStrapUrl = activeUrl
				this.state.currentStrapName = activeName
			}

			const nameElement = strapInfo.querySelector('.strap-name')
			const priceElement = this.querySelector('.strap-price')

			if (nameElement) {
				const suffix = activeAvailable ? '' : this.state.currentWatchDiscontinued ? ' — Sold Out' : ' — Out of stock'
				if (this.mode === 'watches' && activeUrl) {
					const a = document.createElement('a')
					a.href = activeUrl
					a.textContent = activeName
					nameElement.replaceChildren(a, document.createTextNode(suffix))
				} else {
					nameElement.textContent = `${activeName}${suffix}`
				}
			}
			if (priceElement) {
				priceElement.textContent = this.formatMoney(activePriceCents, this.currency)
			}

			strapInfo.classList.toggle('is-sold-out', !activeAvailable)

			if (this.notifyLink) {
				let notifyUrl = null
				if (this.mode === 'watches') {
					if (!this.state.currentStrapAvailable && this.state.currentStrapUrl) {
						notifyUrl = this.state.currentStrapUrl
					} else if (!activeAvailable && activeUrl) {
						notifyUrl = activeUrl
					}
				} else if (!activeAvailable && activeUrl) {
					notifyUrl = activeUrl
				}
				if (notifyUrl && !this.state.currentWatchDiscontinued) {
					this.notifyLink.href = notifyUrl
					this.notifyLink.hidden = false
				} else {
					this.notifyLink.hidden = true
				}
			}

			this.updatePurchaseForms()
		}

		updatePurchaseForms() {
			const strapAvailable = this.state.currentStrapAvailable
			const watchAvailable = this.state.currentWatchAvailable

			// Per-product hide-logic (mode-agnostic):
			//   - watch-only-form depends on watch availability only
			//   - strap-only-form depends on strap availability only
			//   - bundle-form requires BOTH available
			if (this.watchOnlyForm) this.watchOnlyForm.hidden = !watchAvailable
			if (this.strapOnlyForm) this.strapOnlyForm.hidden = !strapAvailable
			if (this.bundleForm) this.bundleForm.hidden = !watchAvailable || !strapAvailable

			this.updateForm(this.watchOnlyForm, 'id', this.state.currentWatchVariantId, !this.state.currentWatchVariantId || !watchAvailable)

			const strapBuyable = this.state.currentStrapVariantId && strapAvailable
			this.updateForm(this.strapOnlyForm, 'id', this.state.currentStrapVariantId, !strapBuyable)

			if (this.bundleForm) {
				const watchInput = this.bundleForm.querySelector('input[name="items[0][id]"]')
				const strapInput = this.bundleForm.querySelector('input[name="items[1][id]"]')
				const button = this.bundleForm.querySelector('button')

				if (watchInput) watchInput.value = this.state.currentWatchVariantId
				if (strapInput) strapInput.value = this.state.currentStrapVariantId

				const bundleBuyable = this.state.currentWatchVariantId && this.state.currentStrapVariantId && this.state.currentStrapAvailable && this.state.currentWatchAvailable
				if (button) {
					button.disabled = !bundleBuyable
					const baseText = button.dataset.baseText || ''
					if (bundleBuyable && baseText) {
						const total = this.state.currentWatchPriceCents + this.state.currentStrapPriceCents
						button.textContent = `${baseText} — ${this.formatMoney(total, this.currency)}`
					} else if (baseText) {
						button.textContent = baseText
					}
				}
			}
		}

		updateForm(form, inputName, value, disabled) {
			if (!form) return

			const input = form.querySelector(`input[name="${inputName}"]`)
			const button = form.querySelector('button')

			if (input) input.value = value || ''
			if (button) button.disabled = disabled
		}

		disconnectedCallback() {
			this.cancelManagedAsync()
			this.destroySwiper()
			window.removeEventListener('resize', this._onResize)
			document.removeEventListener('shopify:section:load', this._onSectionLoad)
			document.removeEventListener('DOMContentLoaded', this._onDOMReady)
			if (this.styleTablist) {
				this.styleTablist.removeEventListener('keydown', this._onStyleTablistKey)
			}
			if (this.faceTablist) {
				this.faceTablist.removeEventListener('keydown', this._onFaceTablistKey)
			}
			if (this.observer) {
				this.observer.disconnect()
			}
			if (this._hydrationObserver) {
				this._hydrationObserver.disconnect()
			}
		}
	}

	customElements.define('watch-strap-configurator-v2', WatchStrapConfiguratorV2)
}
